import { TaskStatus } from '@lit/task';
import type { ReactiveControllerHost, TemplateResult } from 'lit';

import type { IApiFailure } from './api_client/request.js';
import type { IDisplayNameDetail } from './component_events.js';
import { setDocumentTitle } from './document_title.js';
import { isRecordUnavailable } from './http_status.js';
import { type IResourceErrorCopy, resourceErrorHeading, resourceErrorState } from './resource_error_state.js';

/** What a tab page declares of the record its master-data leaf loads. */
interface IMasterDataReportOptions {

    /** The record the page is open on, which a report has to name to count. */
    entityId: () => string;

    /** The page's wording for a record it cannot open. */
    errorCopy: IResourceErrorCopy;

    /** Where the page's access snapshot stands. */
    accessStatus: () => TaskStatus;

    /** Takes what else the report carries onto the page. */
    onReported: (detail: IDisplayNameDetail) => void;
}

/**
 * What a tab page knows of its record from the master-data leaf, which loads it: the name the
 * heading shows, and whether the load failed and how.
 *
 * Each report is held to the record the page is open on, so a late dispatch from the leaf's
 * mount for the previous record cannot name or fail the new one when the user navigates
 * between records.
 */
export class MasterDataReport {
    private name: string | null = null;

    private failed = false;

    private failure: IApiFailure | null = null;

    /**
     * @param host - The tab page, updated on every report.
     * @param options - What the page declares of its record.
     */
    constructor (
        private readonly host: ReactiveControllerHost,
        private readonly options: IMasterDataReportOptions
    ) {}

    /**
     * The record's name, null until the leaf reported it.
     * @returns The name.
     */
    get displayName (): string | null {
        return this.name;
    }

    /**
     * Whether the leaf reported a failed load since the last subject change, whatever its
     * shape, a transport error without a status included. Cleared by a later name.
     * @returns Whether the load failed.
     */
    get loadFailed (): boolean {
        return this.failed;
    }

    /**
     * Whether the heading is still waiting for the leaf: neither a name nor a failure yet.
     * @returns Whether the heading's skeleton stands.
     */
    get headingPending (): boolean {
        return this.name === null && !this.failed;
    }

    /**
     * Whether the tab strip stays behind its skeleton: the leaf is kept mounted to run its
     * fetch, the 403/404 authority, until it reports. An errored access snapshot ends the wait,
     * the orchestration being skipped there and the leaf never reporting.
     * @returns Whether to defer revealing the tab strip.
     */
    get tabsPending (): boolean {
        return this.headingPending && this.options.accessStatus() !== TaskStatus.ERROR;
    }

    /** Forgets the previous record, from the page's `willUpdate` on a subject change. */
    restart (): void {
        this.name = null;
        this.failed = false;
        this.failure = null;
    }

    readonly handleChanged = (event: HTMLElementEventMap['fm-display-name-changed']): void => {
        if (event.detail.entityId !== this.options.entityId()) {
            return;
        }

        this.name = event.detail.name;
        this.failed = false;
        this.failure = null;
        this.options.onReported(event.detail);
        this.host.requestUpdate();
    };

    readonly handleFailed = (event: HTMLElementEventMap['fm-display-name-failed']): void => {
        if (event.detail.entityId !== this.options.entityId()) {
            return;
        }

        const { failure } = event.detail;

        this.failed = true;
        this.failure = failure;

        if (failure !== null && isRecordUnavailable(failure.status)) {
            setDocumentTitle(resourceErrorHeading(failure, this.options.errorCopy));
        }

        this.host.requestUpdate();
    };

    /**
     * The state a record the reader cannot open leaves the page in, or `null` where the failure
     * is neither: the page then renders as usual, the leaf's own callout reporting it.
     *
     * The state replaces the page rather than standing in it, the header, the tab strip and every
     * tab body being about a record that could not be named.
     * @param areaPath - Where the way back leads.
     * @returns The state, or `null` where the page still stands.
     */
    errorState (areaPath: string): TemplateResult | null {
        const { failure } = this;

        if (failure === null || !isRecordUnavailable(failure.status)) {
            return null;
        }

        return resourceErrorState(failure, this.options.errorCopy, areaPath);
    }
}
